/**
 * AirspaceControls Component - Toggle buttons for the airport markers and Points of Interest overlays.
 * (The TMA / Prohibited toggles for OpenAIP polygons were removed in PR6 — the backend returns no
 * polygons, so they were never shown.)
 */
import { Eye, EyeOff, MapPin } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * AirspaceToggleButton - Individual toggle for airspace layer
 */
export const AirspaceToggleButton = ({ 
  isActive, 
  onClick, 
  activeColor = 'bg-blue-600 hover:bg-blue-700',
  label,
  testId,
  icon: CustomIcon
}) => {
  return (
    <Button
      type="button"
      variant={isActive ? "default" : "outline"}
      size="sm"
      onClick={onClick}
      className={`gap-1.5 shadow-md text-xs h-7 px-2 ${
        isActive 
          ? `${activeColor} text-white` 
          : 'bg-white hover:bg-slate-50 text-slate-700'
      }`}
      data-testid={testId}
    >
      {CustomIcon ? (
        <>
          <CustomIcon className="h-3 w-3" />
          <span className="hidden sm:inline">{label}</span>
        </>
      ) : isActive ? (
        <>
          <Eye className="h-3 w-3" />
          <span className="hidden sm:inline">{label}</span>
        </>
      ) : (
        <>
          <EyeOff className="h-3 w-3" />
          <span className="hidden sm:inline">{label}</span>
        </>
      )}
    </Button>
  );
};

/**
 * AirspaceControlPanel - Full panel with all airspace toggle controls
 */
export const AirspaceControlPanel = ({
  showAirspaceLayer,
  setShowAirspaceLayer,
  showCriticalObjects,
  setShowCriticalObjects,
  showAirspaceZones,
  language = 'cs'
}) => {
  const labels = {
    cs: {
      airports: 'Letiště',
      pointsOfInterest: 'Body zájmu'
    },
    en: {
      airports: 'Airports',
      pointsOfInterest: 'Points of Interest'
    }
  };
  
  const t = labels[language] || labels.cs;
  
  return (
    <>
      {/* Airspace Layer Toggle */}
      {showAirspaceZones && (
        <AirspaceToggleButton
          isActive={showAirspaceLayer}
          onClick={() => setShowAirspaceLayer(!showAirspaceLayer)}
          activeColor="bg-blue-600 hover:bg-blue-700"
          label={t.airports}
          testId="airspace-layer-toggle"
        />
      )}
      
      {/* Points of Interest Toggle */}
      {showAirspaceZones && setShowCriticalObjects && (
        <AirspaceToggleButton
          isActive={showCriticalObjects}
          onClick={() => setShowCriticalObjects(!showCriticalObjects)}
          activeColor="bg-sky-500 hover:bg-sky-600"
          label={t.pointsOfInterest}
          testId="poi-toggle"
          icon={MapPin}
        />
      )}
    </>
  );
};

export default AirspaceControlPanel;
